// src/views/folders/slice/slice.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { FolderTreeItem, FolderStatistics, BreadcrumbItemType } from '../../../services/ApiService';

interface InitialStateType {
  folderTree: FolderTreeItem[];
  folderTreeWithLists: FolderTreeItem[];
  isLoadingTree: boolean;
  treeError: string | null;
  isCreatingFolder: boolean;
  createFolderError: string | null;
  isDeletingFolder: boolean;
  deleteFolderError: string | null;
  isMovingItem: boolean;
  moveItemError: string | null;
  isRenamingItem: boolean;
  renameItemError: string | null;
  expandedFolders: string[];
  selectedItem: string | null;
  currentFolderId: string | null;
  breadcrumbs: BreadcrumbItemType[];
  searchQuery: string;
  searchResults: FolderTreeItem[];
  isSearching: boolean;
  statistics: FolderStatistics | null;
  viewMode: 'tree' | 'grid' | 'list';
  sortBy: 'name' | 'date' | 'size';
  sortOrder: 'asc' | 'desc';
  cacheTimestamp: number | null;
  needsRefresh: boolean;
  loadedFolders: string[];
  loadingFolders: string[];
}

export const INITIAL_STATE: InitialStateType = {
  folderTree: [],
  folderTreeWithLists: [],
  isLoadingTree: false,
  treeError: null,
  isCreatingFolder: false,
  createFolderError: null,
  isDeletingFolder: false,
  deleteFolderError: null,
  isMovingItem: false,
  moveItemError: null,
  isRenamingItem: false,
  renameItemError: null,
  expandedFolders: [],
  selectedItem: null,
  currentFolderId: null,
  breadcrumbs: [],
  searchQuery: '',
  searchResults: [],
  isSearching: false,
  statistics: null,
  viewMode: 'tree',
  sortBy: 'name',
  sortOrder: 'asc',
  cacheTimestamp: null,
  needsRefresh: false,
  loadedFolders: [],
  loadingFolders: [],
};

const { name, reducer, ...rest } = createSlice({
  name: 'folders',
  initialState: INITIAL_STATE,
  reducers: {
    // Tree loading
    startLoadingTree: (state) => {
      state.isLoadingTree = true;
      state.treeError = null;
    },

    setTreeData: (state, action: PayloadAction<FolderTreeItem[]>) => {
      state.folderTree = action.payload;
    },

    setTreeComplete: (state, action: PayloadAction<{
      data: FolderTreeItem[];
      timestamp: number;
    }>) => {
      state.folderTreeWithLists = action.payload.data;
      state.cacheTimestamp = action.payload.timestamp;
      state.needsRefresh = false;
      state.isLoadingTree = false;
    },

    setTreeError: (state, action: PayloadAction<string>) => {
      state.treeError = action.payload;
      state.isLoadingTree = false;
    },

    setNeedsRefresh: (state, action: PayloadAction<boolean>) => {
      state.needsRefresh = action.payload;
    },

    // Folder creation
    startCreatingFolder: (state) => {
      state.isCreatingFolder = true;
      state.createFolderError = null;
    },

    finishCreatingFolder: (state) => {
      state.isCreatingFolder = false;
    },

    setCreateFolderError: (state, action: PayloadAction<string>) => {
      state.createFolderError = action.payload;
      state.isCreatingFolder = false;
    },

    // Folder deletion
    startDeletingFolder: (state) => {
      state.isDeletingFolder = true;
      state.deleteFolderError = null;
    },

    finishDeletingFolder: (state) => {
      state.isDeletingFolder = false;
    },

    setDeleteFolderError: (state, action: PayloadAction<string>) => {
      state.deleteFolderError = action.payload;
      state.isDeletingFolder = false;
    },

    // Renaming
    startRenamingItem: (state) => {
      state.isRenamingItem = true;
      state.renameItemError = null;
    },

    finishRenamingItem: (state) => {
      state.isRenamingItem = false;
    },

    setRenameItemError: (state, action: PayloadAction<string>) => {
      state.renameItemError = action.payload;
      state.isRenamingItem = false;
    },

    // Moving items
    startMovingItem: (state) => {
      state.isMovingItem = true;
      state.moveItemError = null;
    },

    finishMovingItem: (state) => {
      state.isMovingItem = false;
    },

    setMoveItemError: (state, action: PayloadAction<string>) => {
      state.moveItemError = action.payload;
      state.isMovingItem = false;
    },

    // Navigation
    setCurrentFolder: (state, action: PayloadAction<string | null>) => {
      state.currentFolderId = action.payload;
    },

    setBreadcrumbs: (state, action: PayloadAction<BreadcrumbItemType[]>) => {
      state.breadcrumbs = action.payload;
    },

    // Search
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    },

    startSearching: (state) => {
      state.isSearching = true;
    },

    setSearchResults: (state, action: PayloadAction<FolderTreeItem[]>) => {
      state.searchResults = action.payload;
      state.isSearching = false;
    },

    // Statistics
    setStatistics: (state, action: PayloadAction<FolderStatistics>) => {
      state.statistics = action.payload;
    },

    // UI state
    toggleFolder: (state, action: PayloadAction<string>) => {
      const folderId = action.payload;
      const index = state.expandedFolders.indexOf(folderId);

      if (index > -1) {
        state.expandedFolders = state.expandedFolders.filter((id) => id !== folderId);
      } else {
        state.expandedFolders = [...state.expandedFolders, folderId];
      }

      localStorage.setItem('expandedFolders', JSON.stringify(state.expandedFolders));
    },

    setSelectedItem: (state, action: PayloadAction<string | null>) => {
      if (state.selectedItem !== action.payload) {
        state.selectedItem = action.payload;
      }
    },

    expandAllFolders: (state) => {
      const getAllFolderIds = (items: FolderTreeItem[]): string[] => items.reduce((ids: string[], item) => {
        if (item.type === 'folder') {
          const childIds = item.children ? getAllFolderIds(item.children) : [];

          return [...ids, item.id, ...childIds];
        }

        return ids;
      }, []);

      const allFolderIds = getAllFolderIds(state.folderTreeWithLists);
      state.expandedFolders = allFolderIds;
      localStorage.setItem('expandedFolders', JSON.stringify(allFolderIds));
    },

    collapseAllFolders: (state) => {
      state.expandedFolders = [];
      localStorage.removeItem('expandedFolders');
    },

    setViewMode: (state, action: PayloadAction<'tree' | 'grid' | 'list'>) => {
      state.viewMode = action.payload;
      localStorage.setItem('folderViewMode', action.payload);
    },

    setSortBy: (state, action: PayloadAction<'name' | 'date' | 'size'>) => {
      state.sortBy = action.payload;
    },

    setSortOrder: (state, action: PayloadAction<'asc' | 'desc'>) => {
      state.sortOrder = action.payload;
    },

    startLoadingFolder: (state, action: PayloadAction<string>) => {
      if (!state.loadingFolders.includes(action.payload)) {
        state.loadingFolders = [...state.loadingFolders, action.payload];
      }
    },

    finishLoadingFolder: (state, action: PayloadAction<string>) => {
      state.loadingFolders = state.loadingFolders.filter((id) => id !== action.payload);
    },

    setFolderChildren: (state, action: PayloadAction<{
      folderId: string;
      children: FolderTreeItem[];
    }>) => {
      const { folderId, children } = action.payload;

      const updateChildren = (nodes: FolderTreeItem[]): FolderTreeItem[] => nodes.map((node) => {
        if (node.id === folderId) {
          return { ...node, children };
        }
        if (node.children) {
          return { ...node, children: updateChildren(node.children) };
        }

        return node;
      });

      state.folderTreeWithLists = updateChildren(state.folderTreeWithLists);

      if (!state.loadedFolders.includes(folderId)) {
        state.loadedFolders = [...state.loadedFolders, folderId];
      }
      state.loadingFolders = state.loadingFolders.filter((id) => id !== folderId);
    },

    resetFoldersState: () => INITIAL_STATE,
  },
});

export default {
  name,
  reducer,
  ...rest,
};
